import React from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography
} from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import PublishIcon from '@material-ui/icons/Publish';
import CreditCardIcon from '@material-ui/icons/CreditCard';
import LockIcon from '@material-ui/icons/Lock';

const useStyles = makeStyles((theme) => ({
  dialogPaper: {
    borderRadius: theme.spacing(2),
    boxShadow: theme.shadows[6]
  },
  dialogTitle: {
    padding: theme.spacing(3, 3, 1.5)
  },
  titleContent: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(3)
  },
  titleText: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5)
  },
  titleIcon: {
    color: theme.palette.primary.main,
    fontSize: 26
  },
  titleSubtitle: {
    fontSize: '0.9rem',
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.75),
    lineHeight: 1.5
  },
  trustChip: {
    borderRadius: theme.spacing(2),
    fontWeight: 600,
    backgroundColor: theme.palette.background.default,
    borderColor: theme.palette.divider
  },
  dividerLine: {
    height: 1,
    backgroundColor: theme.palette.divider,
    margin: theme.spacing(0, 3)
  },
  dialogContent: {
    padding: theme.spacing(2.5, 3, 3.5)
  },
  mainText: {
    fontSize: '1rem',
    marginBottom: theme.spacing(2.5),
    lineHeight: 1.7
  },
  infoBox: {
    padding: theme.spacing(2.25),
    background: theme.palette.background.default,
    borderRadius: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
    marginBottom: theme.spacing(2.5),
    boxShadow: theme.shadows[1]
  },
  infoRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(1.25),
    '&:last-child': {
      marginBottom: 0
    }
  },
  inlineSummary: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1.5),
    alignItems: 'center'
  },
  inlineItem: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.75)
  },
  inlineDivider: {
    color: theme.palette.text.disabled,
    fontWeight: 700
  },
  infoLabel: {
    fontSize: '0.875rem',
    color: theme.palette.text.secondary,
    fontWeight: 500
  },
  infoValue: {
    fontSize: '0.875rem',
    color: theme.palette.text.primary,
    fontWeight: 600
  },
  creditsChip: {
    fontWeight: 600,
    fontSize: '0.875rem'
  },
  warningText: {
    fontSize: '0.875rem',
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(1.5),
    lineHeight: 1.6
  },
  dialogActions: {
    padding: theme.spacing(2.5, 3, 3)
  },
  confirmButton: {
    padding: theme.spacing(0.75, 2),
    fontWeight: 700,
    textTransform: 'none',
    borderRadius: 999,
    boxShadow: '0 12px 24px -18px rgba(63, 81, 181, 0.6)',
    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
    '&:hover': {
      transform: 'translateY(-1px)',
      boxShadow: '0 16px 28px -18px rgba(63, 81, 181, 0.7)'
    }
  },
  cancelButton: {
    padding: theme.spacing(0.75, 2),
    textTransform: 'none',
    borderRadius: 999,
    fontWeight: 700
  }
}));

const PublishJobConfirmationDialog = ({
  open,
  onClose,
  onConfirm,
  jobTitle,
  planName,
  remainingCredits,
  isPublishing,
  hasPostJobPlanPaid
}) => {
  const classes = useStyles();
  const shouldShowCreditUsage = hasPostJobPlanPaid === false;

  const handleConfirm = () => {
    onConfirm();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="publish-confirmation-dialog-title"
      classes={{ paper: classes.dialogPaper }}>
      <DialogTitle
        id="publish-confirmation-dialog-title"
        className={classes.dialogTitle}>
        <Box className={classes.titleContent}>
          <Box>
            <Box className={classes.titleText}>
              <PublishIcon className={classes.titleIcon} />
              <Typography variant="h6" component="span">
                Ready to publish?
              </Typography>
            </Box>
            <Typography className={classes.titleSubtitle}>
              Your job goes live immediately after confirmation.
            </Typography>
          </Box>
          <Chip
            icon={<LockIcon style={{ fontSize: 16 }} />}
            label="Secure payment"
            size="small"
            variant="outlined"
            className={classes.trustChip}
          />
        </Box>
      </DialogTitle>
      <Box className={classes.dividerLine} />

      <DialogContent className={classes.dialogContent}>
        <Typography className={classes.mainText}>
          Publish <strong>"{jobTitle}"</strong> now.
          {shouldShowCreditUsage ? (
            <>
              {' '}
              This will use <strong>1 job credit</strong>.
            </>
          ) : (
            <> No additional credits are needed.</>
          )}
        </Typography>

        <Box className={classes.infoBox}>
          <Box className={classes.inlineSummary}>
            {shouldShowCreditUsage && (
              <>
                <Box className={classes.inlineItem}>
                  <Typography className={classes.infoLabel}>
                    Credits Before
                  </Typography>
                  <Chip
                    icon={<CreditCardIcon style={{ fontSize: 16 }} />}
                    label={`${remainingCredits} credits`}
                    size="small"
                    color="primary"
                    className={classes.creditsChip}
                  />
                </Box>
                <Typography className={classes.inlineDivider}>•</Typography>
                <Box className={classes.inlineItem}>
                  <Typography className={classes.infoLabel}>
                    Credits After
                  </Typography>
                  <Chip
                    icon={<CreditCardIcon style={{ fontSize: 16 }} />}
                    label={`${remainingCredits - 1} credits`}
                    size="small"
                    color="default"
                    className={classes.creditsChip}
                  />
                </Box>
              </>
            )}
          </Box>
        </Box>

        <Typography className={classes.warningText}>
          You can unpublish anytime without losing the credit used for this job.
        </Typography>
      </DialogContent>

      <DialogActions className={classes.dialogActions}>
        <Button
          onClick={onClose}
          color="default"
          variant="outlined"
          className={classes.cancelButton}
          disabled={isPublishing}>
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          color="primary"
          variant="contained"
          className={classes.confirmButton}
          startIcon={<PublishIcon />}
          disabled={isPublishing}>
          {isPublishing ? 'Publishing...' : 'Publish job'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

PublishJobConfirmationDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  jobTitle: PropTypes.string,
  planName: PropTypes.string,
  remainingCredits: PropTypes.number,
  isPublishing: PropTypes.bool,
  hasPostJobPlanPaid: PropTypes.bool
};

PublishJobConfirmationDialog.defaultProps = {
  jobTitle: 'this job',
  planName: 'Unknown',
  remainingCredits: 0,
  isPublishing: false,
  hasPostJobPlanPaid: false
};

export default PublishJobConfirmationDialog;
